import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import {
  createConsultationCompletionDetails,
  updateConsultationCompletionDetails,
} from "~/providers/api";
import { useGetPatient } from "~/providers/hooks";
import {
  UpdateConsultationCompletionDetailsProps,
  UpdateConsultationDetails,
  UpsertConsultationCompletionMutationProps,
} from "~/providers/utils/types/consultation";

interface UseUpsertConsultationDetails {
  patientId: string;
  saveChangesAutomatically: boolean;
  setSaveChangesAutomatically: (value: boolean) => void;
}

export const useUpsertConsultationCompletionDetails = ({
  patientId,
  saveChangesAutomatically,
  setSaveChangesAutomatically,
}: UseUpsertConsultationDetails) => {
  const queryClient = useQueryClient();

  const handleSuccess = async (successMessage: string) => {
    setSaveChangesAutomatically(true);
    await queryClient.invalidateQueries({
      queryKey: ["getConsultationCompletionDetail", patientId, consultationId],
    });
    !saveChangesAutomatically && toast.success(successMessage);
  };

  const { data: patientData } = useGetPatient(patientId);
  const consultationId = patientData?.consultation?.id;

  const {
    mutate: updateConsultationCompletionMutation,
    isPending: pendingUpdate,
  } = useMutation({
    mutationFn: ({
      consultationDetails,
      consultationDetailsId,
    }: UpdateConsultationCompletionDetailsProps) =>
      updateConsultationCompletionDetails({
        consultationId,
        consultationDetails,
        consultationDetailsId,
      }),
    onSuccess: async () =>
      await handleSuccess("Consultation details saved successfully!"),
    onError: () =>
      toast.error(
        "We shoot trouble saving the information, is the issue persists please reload the page!",
      ),
  });

  const {
    mutate: createConsultationCompletionMutation,
    isPending: pendingCreate,
  } = useMutation({
    mutationFn: (consultationDetails: UpdateConsultationDetails) =>
      createConsultationCompletionDetails({
        consultationId,
        consultationDetails,
      }),
    onSuccess: async () =>
      await handleSuccess("Consultation details saved successfully!"),
    onError: () =>
      toast.error(
        "We shoot trouble saving the information, is the issue persists please reload the page!",
      ),
  });

  const isPending = pendingUpdate || pendingCreate;

  const upsertConsultationCompletionMutation = ({
    data,
    existingData,
  }: UpsertConsultationCompletionMutationProps) =>
    existingData
      ? updateConsultationCompletionMutation({
          consultationDetails: data,
          consultationDetailsId: existingData.id,
        })
      : createConsultationCompletionMutation(data);

  return { upsertConsultationCompletionMutation, isPending };
};
